'use client'

import * as React from 'react'
import { clsx } from 'clsx'
import {
  useParams,
  usePathname,
  useSelectedLayoutSegment,
} from 'next/navigation'

import { ButtonLink } from '../../../sotano-kit/primitives/button'
import { HeaderMiddleLinksFragment } from './links'

export const LightHeaderNav = ({
  headerLinks,
}: {
  headerLinks: HeaderMiddleLinksFragment[]
}) => {
  const selectedLayoutSegment = useSelectedLayoutSegment()
  const navRef = React.useRef<HTMLDivElement>(null)
  const lightRef = React.useRef<HTMLDivElement>(null)
  const path = usePathname()
  const isHighlightingSegment = [
    'changelog',
    'blog',
    'manifesto',
    null,
    'home',
    'docs',
    'templates',
    'pricing',
  ].includes(selectedLayoutSegment)
  const isFirstHighlighterStyleMutationRef = React.useRef(true)

  const [hash, setHash] = React.useState<string | undefined | null>(() =>
    typeof window !== 'undefined' ? window.location.hash : undefined
  )
  const params = useParams()

  React.useEffect(() => {
    setHash(window.location.hash)
  }, [params])

  React.useEffect(() => {
    const activeLink = navRef.current?.querySelector(
      '[data-active="true"]'
    ) satisfies HTMLAnchorElement | null | undefined
    if (activeLink && lightRef.current && navRef.current) {
      const navBounding = navRef.current.getBoundingClientRect()
      const bounding = activeLink.getBoundingClientRect()

      if (isFirstHighlighterStyleMutationRef.current) {
        lightRef.current.style.transition = 'opacity 0.3s ease-in-out'
        isFirstHighlighterStyleMutationRef.current = false
      } else {
        lightRef.current.style.transition =
          'opacity 0.3s ease-in-out, transform 0.3s ease-in-out, width 0.3s ease-in-out'
      }
      lightRef.current.style.opacity = '1'
      lightRef.current.style.transform = `translateX(${
        bounding.left - navBounding.left
      }px)`

      lightRef.current.style.width = `${bounding.width}px`
    } else if (!activeLink && lightRef.current) {
      lightRef.current.style.opacity = '0'
    }
  }, [path, hash])

  return (
    <nav
      className="relative flex h-full w-max items-center justify-self-center justify-center"
      ref={navRef}
    >
      <div
        ref={lightRef}
        className={clsx(
          isHighlightingSegment && 'bg-gradient-highlight',
          'absolute -bottom-px h-px left-0'
        )}
        style={{
          display: isHighlightingSegment ? 'block' : 'none',
          opacity: 0,
        }}
      />
      {headerLinks.map((link) => {
        const isActive = selectedLayoutSegment === link.href.split('/')[1]
        return (
          <ButtonLink
            key={link._id}
            unstyled
            focusStyled
            className={clsx(
              'pointer-events-auto relative hidden md:inline-flex h-full select-none items-center px-2.5 text-xs md:text-sm transition-colors duration-300 hover:text-bright',
              isActive ? 'text-bright' : 'text-dark-muted',
              link.new && 'mr-7'
            )}
            href={link.href}
            data-active={isActive}
          >
            {link._title}
            {link.new && (
              <span className="text-[11px] left-[calc(100%-6px)] top-1/2 -translate-y-1/2 font-medium text-dark-control-base bg-dark-control-base/20 rounded-full px-1 absolute">
                New
              </span>
            )}
          </ButtonLink>
        )
      })}
    </nav>
  )
}
